import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { CheckCircle2, XCircle, Clock, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";

// Mollie payment-status page.
// After Mollie checkout, users land here (with ?payment_id=tr_xxx).
// We poll `GET /api/mollie/status/{payment_id}` until the webhook has
// finalized the order (order_status === "activated" AND user_tier upgraded).
// Max ~40 seconds (12 tries at 3.5s) — beyond that, retry / manual continue.
export default function PaymentStatusPage() {
  const t = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  // Mollie voegt zelf `?id=tr_xxx` toe (niet `payment_id`). We accepteren
  // beide varianten én vallen terug op sessionStorage — dat werd bij de
  // start van de checkout gezet door PricingPage. Zo werkt de flow ook als
  // Mollie helemaal geen query-param meestuurt.
  const paymentIdFromUrl = search.get("payment_id") || search.get("id");
  const paymentIdFromStorage = (() => {
    try { return sessionStorage.getItem("mollie_last_payment_id"); } catch { return null; }
  })();
  const paymentId = paymentIdFromUrl || paymentIdFromStorage;
  const [status, setStatus] = useState("polling"); // polling|activated|failed|timeout
  const [orderStatus, setOrderStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [amount, setAmount] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const { refreshMe } = useAuth();
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!paymentId) {
      setStatus("no_id");
      setError(t("We konden geen betaal-referentie vinden. Ga naar je account om je abonnement te controleren."));
      return;
    }
    let cancelled = false;
    const MAX_TRIES = 12;
    async function poll(tries = 0) {
      if (cancelled || stoppedRef.current) return;
      setAttempts(tries + 1);
      try {
        const { data } = await api.get(`/mollie/status/${paymentId}`);
        setOrderStatus(data.order_status);
        setPlan(data.plan);
        setAmount(data.amount);
        if (data.order_status === "activated") {
          stoppedRef.current = true;
          setStatus("activated");
          try { sessionStorage.removeItem("mollie_last_payment_id"); } catch { /* private mode */ }
          try { await refreshMe?.(); } catch (_) {}
          return;
        }
        if (["failed", "canceled", "expired", "chargeback", "charged_back"].includes(data.order_status)) {
          stoppedRef.current = true;
          setStatus("failed");
          try { sessionStorage.removeItem("mollie_last_payment_id"); } catch { /* private mode */ }
          return;
        }
        // States "open", "activating", "mandate_pending", "sub_failed" keep polling.
      } catch (e) {
        // 404 = payment not found (deleted, wrong id, cross-user). Abort polling
        // immediately so users see the proper 'failed' state instead of a 42s
        // spinner.
        if (e?.response?.status === 404) {
          stoppedRef.current = true;
          setStatus("failed");
          setError(formatApiError(e.response?.data?.detail) || t("Betaling niet gevonden."));
          try { sessionStorage.removeItem("mollie_last_payment_id"); } catch { /* private mode */ }
          return;
        }
        setError(formatApiError(e.response?.data?.detail));
      }
      if (tries >= MAX_TRIES) { setStatus("timeout"); return; }
      setTimeout(() => poll(tries + 1), 3500);
    }
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  const planLabel = plan === "STANDARD" ? "Standaard" : plan === "PREMIUM" ? "Premium" : plan;

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto mt-8 border-2 border-foreground bg-card heavy-shadow p-8 text-center" data-testid="payment-status-page">
        {status === "polling" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-primary animate-spin" />
            <div className="stat-label mt-4">{t("Verwerken")}</div>
            <h1 className="font-display font-black text-3xl uppercase mt-1">{t("Betaling wordt bevestigd")}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {t("We wachten op de bevestiging van Mollie. Dit duurt meestal 5-15 seconden.")}
              <br/><span className="text-xs">{t("Poging {n} van {max}").replace("{n}", String(attempts)).replace("{max}", String(12))}</span>
            </p>
            {orderStatus && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="payment-status-order-state">
                {t("Status")}: <span className="font-mono uppercase">{orderStatus}</span>
              </p>
            )}
          </>
        )}
        {status === "activated" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-positive" data-testid="payment-status-success-icon" />
            <div className="stat-label mt-4 text-positive">{t("Gelukt")}</div>
            <h1 className="font-display font-black text-3xl uppercase mt-1">{t("Je abonnement is actief!")}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {plan
                ? t("Bedankt voor je vertrouwen. Je {plan}-abonnement (€{amount}/jaar) is nu actief en wordt volgend jaar automatisch verlengd via SEPA-incasso of creditcard.")
                    .replace("{plan}", planLabel).replace("{amount}", amount || "")
                : t("Bedankt voor je vertrouwen. Je abonnement is nu actief.")}
            </p>
            <button
              type="button"
              onClick={() => nav("/")}
              data-testid="payment-status-continue"
              className="mt-5 btn-brutal bg-primary text-primary-foreground px-5 py-3 font-display uppercase font-black text-sm inline-flex items-center gap-2"
            >
              {t("Naar mijn dashboard")} <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Clock className="w-14 h-14 mx-auto text-muted-foreground" />
            <div className="stat-label mt-4">{t("Nog niet bevestigd")}</div>
            <h1 className="font-display font-black text-3xl uppercase mt-1">{t("Het duurt wat langer dan verwacht")}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {t("Je betaling is verstuurd, maar Mollie heeft de bevestiging nog niet doorgestuurd. Herlaad deze pagina over een minuut — of ga naar je dashboard, we activeren zodra de webhook binnenkomt.")}
            </p>
            <div className="mt-5 flex gap-2 justify-center">
              <button type="button" onClick={() => window.location.reload()}
                data-testid="payment-status-retry"
                className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">
                {t("Opnieuw checken")}
              </button>
              <Link to="/"
                data-testid="payment-status-dashboard"
                className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm">
                {t("Naar dashboard")}
              </Link>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-destructive" data-testid="payment-status-failed-icon" />
            <div className="stat-label mt-4 text-destructive">{t("Betaling niet gelukt")}</div>
            <h1 className="font-display font-black text-3xl uppercase mt-1">{t("Er ging iets mis")}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {orderStatus === "expired" ? t("De betaal-sessie is verlopen. Probeer het opnieuw.") :
               orderStatus === "canceled" ? t("Je hebt de betaling geannuleerd.") :
               t("De betaling is niet gelukt. Controleer je bankgegevens of probeer een andere methode.")}
            </p>
            <Link to="/prijzen" data-testid="payment-status-back-to-pricing"
              className="mt-5 btn-brutal bg-primary text-primary-foreground px-5 py-3 font-display uppercase font-black text-sm inline-flex items-center gap-2">
              {t("Terug naar prijzen")}
            </Link>
          </>
        )}
        {status === "no_id" && (
          <>
            <Clock className="w-14 h-14 mx-auto text-muted-foreground" data-testid="payment-status-no-id-icon" />
            <div className="stat-label mt-4">{t("Bevestiging binnenkort")}</div>
            <h1 className="font-display font-black text-3xl uppercase mt-1">{t("We wachten op de betaalbevestiging")}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {t("Je bent teruggekomen zonder betaal-referentie. Als je zojuist hebt betaald, verwerken we de bevestiging via Mollie op de achtergrond. Meestal binnen 30 seconden zichtbaar op je account.")}
            </p>
            <div className="mt-5 flex gap-2 justify-center flex-wrap">
              <Link to="/account" data-testid="payment-status-noid-account"
                className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
                {t("Naar mijn account")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/" data-testid="payment-status-noid-dashboard"
                className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm">
                {t("Naar dashboard")}
              </Link>
            </div>
          </>
        )}
        {error && <p className="text-xs text-destructive mt-3" data-testid="payment-status-error">{error}</p>}
      </div>
    </AppLayout>
  );
}
